import { Injectable } from '@angular/core';
import { StorageManager } from 'src/app/common/utils/storage';
import { IAuthData } from '../auth';
import {
  IContactData,
  IComplianceItemData,
  IComplianceTasks,
  ISubcontractorData,
  ChangePasswordResult,
  ChangeTelephoneResult,
  ChangeSignedInResult,
  ChangeNameResult,
  ChangeRenewMessageResult,
  ChangeSiteMessageResult,
  IDefectRecords,
  ISiteData,
  IAllSitesData,
  ILinkedSiteAttendance,
  ISiteAttendance,
  ISiteInductions,
  ISiteInspections,
  ISiteSubcontractors,
  ISwmsData,
  IEmployeeData,
  IUserApps,
  IPermits,
  AddProjectResult,
  AddToolboxTalkResult,
  AddContactResult,
  IRiskAssessmentData,
  IToolboxTalkData,
  IToolboxTalks,
  ITraining,
  IConnectionData,
  IPlantRegister,
  IElectricalRegister,
  IHazardousChemicalsRegister,
  IImplementations,
  IIncidentReports,
  INonComplianceNotices,
  IEmailStatus,
  IPlantRegistrations,
  IPlantChecklists,
  ISiteIncidents,
  IScaffoldInspections,
  IFormworkPrepour,
  IMessages,
  IToolboxTalkTopics,
  ISiteHazards,
  IReferences,
  IRegisterItems,
  ItoolboxTalkAttendance,
  ITools
} from './api.interface';
import { CONSTANTS } from 'src/constants';
import { IAccountData } from '.';
import { environment } from "../../../environments/environment";
import axiosInstance from "../axios/axiosInstance";
import { parseComplianceSpLinks } from "../utils";

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private storage = new StorageManager();

  constructor() {
  }

  /**
   * Returns true if the user is logged in before.
   */
  public get isLoggedin(): boolean {
    let userData = this.storage.getCommonObject('userData');
    return !!userData?.data;
  }

  /**
   * Returns the authData in the storage if it exists or did not expire
   */
  private get authData(): IAuthData {
    let localAuthData = this.storage.getCommonObject('authData');
    let authData = localAuthData?.data;

    if (authData && new Date().getTime() > +authData.expires_in * 1000 + localAuthData.lastModify) {
      this.storage.removeCommonObject('authData');
      this.storage.removeCommonObject('userData');
      return undefined;
    }
    return authData;
  }

  /**
   * Returns the userData in the storage if it exists.
   */
  public get userData(): IContactData {
    return this.storage.getCommonObject('userData')?.data;
  }

  /**
   * Fetches all accounts data from Dynamics365
   */
  public async getAccounts(): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/all`
    );

    return res.data.data.value;
  }

  /**
   * Fetches and returns the compliance items by modifying the data in a proper format.
   * @param accountId
   */
  public async getComplianceDetails(accountId: string): Promise<IComplianceItemData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance/${accountId}`
    );

    return parseComplianceSpLinks(res.data.data.value);
  }

  /**
   * Fetches and returns the compliance items (LINKED TO A BUILDER) by modifying the data in a proper format.
   * @param accountId
   */
  public async getLinkedComplianceDetails(accountId: string): Promise<IComplianceItemData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance/linked/${accountId}`,
        {
          params: {
            contactId: this.userData.contactid,
            contactName: this.userData.fullname
          }
        }
    );

    return parseComplianceSpLinks(res.data.data.value);
  }

  /**
   * Fetches and returns the compliance items (LINKED TO A SUBCONTRACTOR) by modifying the data in a proper format.
   * @param accountId
   */
  public async getSubcontractorLinkedComplianceDetails(accountId: string): Promise<IComplianceItemData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance/subcontractor/${accountId}`,
        {
          params: {
            parentAccountId: this.userData.parent_account.accountid,
            parentAccountName: this.userData.parent_account.name
          }
        }
    );

    return parseComplianceSpLinks(res.data.data.value);
  }

  /**
   * Fetches and returns the compliance items by modifying the data in a proper format.
   * @param accountId
   */
  public async getUnlinkedComplianceDetails(accountId: string): Promise<IComplianceItemData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance/unlinked/${accountId}`
    );

    return parseComplianceSpLinks(res.data.data.value);
  }

  /**
   * Create new site
   */
  public async addSite(
      siteName: string,
      jobNumber: string,
      streetAddress: string,
      suburbAddress: string,
      stateAddress: string,
      supervisorContact: string,
      supervisorMobile: string,
      supervisorEmail: string,
      lat: string,
      long: string
  ): Promise<AddProjectResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/sites`,
        {
          siteName: siteName,
          jobNumber: jobNumber,
          streetAddress: streetAddress,
          suburbAddress: suburbAddress,
          stateAddress: stateAddress,
          supervisorContact: supervisorContact,
          supervisorMobile: supervisorMobile,
          supervisorEmail: supervisorEmail,
          lat: lat,
          long: long,
          accountId: this.userData.parent_account.accountid
        }
    );

    return res.data.data;
  }

  /**
   * Resolves lat/long coordinates for an address via the backend geocoding endpoint.
   */
  public async geocodeAddress(
      street: string,
      suburb: string,
      state: string
  ): Promise<{ lat: string; long: string; formattedAddress: string }> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/geocode`,
        { params: { street, suburb, state } }
    );

    return res.data.data;
  }

  /**
   * Create new toolbox talk
   */
  public async newToolboxTalk(siteId: string): Promise<AddToolboxTalkResult> {
    const userData: IContactData = this.storage.getCommonObject('userData').data;

    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/toolbox-talks/new`,
        {
          siteId: siteId,
          supervisorName: userData.fullname
        }
    );

    return res.data.data;
  }

  /**
   * Create new toolbox talk
   */
  public async addToolboxTalk(
      siteId: string,
      start: string,
      subject: string,
      principalContractorTopics: string,
      subcontractorTopics: string,
      end: string,
      duration: number,
      toolboxTalkId: string
  ): Promise<AddToolboxTalkResult> {
    const userData: IContactData = this.storage.getCommonObject('userData').data;

    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/toolbox-talks/${toolboxTalkId}`,
        {
          siteId: siteId,
          start: start,
          subject: subject,
          principalTopics: principalContractorTopics,
          subcontractorTopics: subcontractorTopics,
          end: end,
          duration: duration,
          supervisorName: userData.fullname
        }
    );

    return res.data.data;
  }

  /**
   * Deactivate Toolbox Talk
   * @param toolboxTalkId
   */
  public async deactivateToolboxTalk(toolboxTalkId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/toolbox-talks/deactivate/${toolboxTalkId}`
    );

    return res.data.data;
  }


  /**
   * Deactivate register item
   * @param registerId
   */
  public async deactivateRegisterItem(registerId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/registers/items/${registerId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Reactivate register item
   * @param registerId
   */
  public async reactivateRegisterItem(registerId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/registers/items/${registerId}/reactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate Hazard
   * @param hazardId
   */
  public async deactivateHazard(hazardId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/hazards/deactivate/${hazardId}`
    );

    return res.data.data;
  }

  /**
   * Deactivate Incident
   * @param incidentId
   */
  public async deactivateIncident(incidentId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/incidents/deactivate/${incidentId}`
    );

    return res.data.data;
  }

  /**
   * Deactivate Noncompliance
   * @param noncomplianceId
   */
  public async deactivateNoncompliance(noncomplianceId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/compliance-notices/deactivate/${noncomplianceId}`
    );

    return res.data.data;
  }

  /**
   * Deactivate Defect
   * @param defectId
   */
  public async deactivateDefect(defectId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/defects/deactivate/${defectId}`
    );

    return res.data.data;
  }

  /**
   * Change Hazard Status
   * @param hazardId
   * @param status
   */
  public async changeHazardStatus(hazardId: string, status: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/hazards/${hazardId}/status`,
        {
          status: status
        }
    );

    return res.data.data;
  }

  /**
   * Clones induction and link it to new SWMS
   */
  public async linkInductionToSwms(
      new_name: string,
      new_firstname: string,
      new_lastname: string,
      new_email: string,
      new_phone: string,
      cr421_sharepoint: string,
      cr421_company: string,
      new_inductionid: string,
      swmsId: string,
      siteId: string,
      accountId: string,
      employeeId: string
  ): Promise<AddProjectResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/inductions/link-to-swms`,
        {
          new_name: new_name,
          new_firstname: new_firstname,
          new_lastname: new_lastname,
          new_email: new_email,
          new_phone: new_phone,
          cr421_sharepoint: cr421_sharepoint,
          cr421_company: cr421_company,
          new_inductionid: new_inductionid,
          swmsId: swmsId,
          siteId: siteId,
          accountId: accountId,
          employeeId: employeeId
        }
    );

    return res.data.data;
  }

  /**
   * Add new contact record (REGISTER NEW ACCOUNT)
   * @param email
   * @param password
   * @param firstname
   * @param lastname
   */
  public async addContact(
      email: string,
      password: string,
      firstname: string,
      lastname: string
  ): Promise<AddContactResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/contacts`,
        {
          email: email,
          password: password,
          firstname: firstname,
          lastname: lastname
        }
    );

    return res.data.data;
  }

  /**
   * Add new contractor agreement
   * @param accountId
   * @param contractorId
   */
  public async addContractorAgreement(accountId: string, contractorId: string): Promise<AddProjectResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/connections/contractor-agreement`,
        {
          accountId: accountId,
          contractorId: contractorId
        }
    );

    return res.data.data;
  }

  /**
   * Add new register to reference table
   * @param siteGuid
   * @param category
   * @param name
   */
  public async addRegister(siteGuid: string, category: string, name: string): Promise<AddProjectResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/registers/add`,
        {
          siteGuid: siteGuid,
          category: category,
          name: name
        }
    );

    return res.data.data;
  }

  /**
   * Add audit log
   * @param table
   * @param reference
   * @param json
   */
  public async addAudit(table: string, reference: string, json: any): Promise<AddProjectResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/audits`,
        {
          table: table,
          reference: reference,
          json: json
        }
    );

    return res.data.data;
  }

  /**
   * Add new toolbox talk topic
   * @param topic
   * @param siteId
   */
  public async addToolboxTalkTopic(topic: string, siteId: string): Promise<AddProjectResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/toolbox-talks/topics`,
        {
          topic: topic,
          siteId: siteId
        }
    );

    return res.data.data;
  }

  /**
   * Change statement date in account record to trigger contract agreement flow
   * @param accountId
   */
  public async changeStatementDate(accountId: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/connections/${accountId}/statement-date`
    );

    return res.data.data;
  }

  /**
   * Connect subcontractors to site
   * @param siteId
   * @param accountId
   */
  public async connectSiteSubcontractor(siteId: string, accountId: string): Promise<AddToolboxTalkResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/accounts/connect-site-subcontractor`,
        {
          siteId: siteId,
          accountId: accountId
        }
    );

    return res.data.data;
  }

  /**
   * Remove relationship between SUBCONTRACTOR and PROJECT
   * @param connectedProjectId
   */
  public async disconnectSubcontractorFromSite(connectedProjectId: string): Promise<AddToolboxTalkResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/accounts/disconnect-subcontractor/${connectedProjectId}`
    );

    return res.data.data;
  }

  /**
   * Deactivate INDUCTION from SWMS
   * @param inductionId
   */
  public async deleteInduction(inductionId: string): Promise<AddToolboxTalkResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/inductions/deactivate/${inductionId}`
    );

    return res.data.data;
  }

  /**
   * Add toolbox talk attendance for current user
   * @param toolboxTalkId
   */
  public async addToolboxTalkAttendance(toolboxTalkId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/toolbox-talks/attendance/single`,
        {
          toolboxTalkId: toolboxTalkId,
          contactId: this.userData.contactid,
          accountId: this.userData.parent_account.accountid
        }
    );

    return res.data.data;
  }

  /**
   * Add toolbox talk attendance by contactid and toolboxtalk id (MULTIPLE ATTENDANCE)
   * @param contactId
   * @param accountId
   * @param toolboxTalkId
   */
  public async addToolboxTalkAttendances(
      contactId: string,
      accountId: string,
      toolboxTalkId: string
  ): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/toolbox-talks/attendance`,
        {
          contactId: contactId,
          accountId: accountId,
          toolboxTalkId: toolboxTalkId
        }
    );

    return res.data.data;
  }

  /**
   * Add SMS Message
   * @param subject
   * @param message
   * @param siteId
   * @param contactId
   * @param mobile
   * @param sentBy
   * @param sentByEmail
   */
  public async addSmsMessage(
      subject: string,
      message: string,
      siteId: string,
      contactId: string,
      mobile: string,
      sentBy: string,
      sentByEmail: string
  ): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/messages/sms`,
        {
          subject: subject,
          message: message,
          siteId: siteId,
          contactId: contactId,
          mobile: mobile,
          sentBy: sentBy,
          sentByEmail: sentByEmail
        }
    );

    return res.data.data;
  }

  /**
   * Add Email Message
   * @param subject
   * @param message
   * @param siteId
   * @param contactId
   * @param email
   * @param sentBy
   * @param sentByEmail
   */
  public async addEmailMessage(
      subject: string,
      message: string,
      siteId: string,
      contactId: string,
      email: string,
      sentBy: string,
      sentByEmail: string
  ): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/messages/email`,
        {
          subject: subject,
          message: message,
          siteId: siteId,
          contactId: contactId,
          email: email,
          sentBy: sentBy,
          sentByEmail: sentByEmail
        }
    );

    return res.data.data;
  }

  /**
   * changes the password of the current user.
   */
  public async changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResult> {
    const response = await axiosInstance.post<ChangePasswordResult>(
        '/auth/change-password',
        {
          currentPassword,
          newPassword
        }
    );

    return response.data;
  }

  /**
   * Updates CR421 token of the user
   * @param username
   */
  public async setToken(username: string): Promise<ChangePasswordResult> {
    let user = await this.getContact(username);

    if (user) {
      const res = await axiosInstance.patch(
          `${environment.backendApiUrl}/contacts/${user.contactid}/set-token`
      );

      return res.data.data;
    }

    throw new Error('User not found');
  }

  /**
   * Reset password function
   * @param contactId
   * @param token
   * @param password - plain text password (will be hashed on backend)
   */
  public async resetPassword(contactId: string, token: string, password: string): Promise<ChangePasswordResult> {
    const res = await axiosInstance.post(
        `${environment.backendApiUrl}/user/${contactId}/reset-password`,
        {
          token: token,
          password: password
        }
    );

    return res.data.data;
  }

  /**
   * Deactivate user
   * @param contactId
   */
  public async deactivateUser(contactId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/contacts/${contactId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate training record
   * @param trainingId
   */
  public async deactivateTrainingRecord(trainingId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/training/${trainingId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate Document
   * @param documentId
   */
  public async deactivateSiteDocument(documentId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/documents/${documentId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate Site Swms (IN DOCUMENTS)
   * @param swmsId
   */
  public async deactivateSiteSwms(swmsId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/swms/${swmsId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate plant register record
   * @param plantId
   */
  public async deactivatePlant(plantId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/plants/${plantId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Activate plant register record
   * @param plantId
   */
  public async activatePlant(plantId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/plants/${plantId}/activate`
    );

    return res.data.data;
  }

  /**
   * Close non-compliance notice
   * @param noticeId
   */
  public async closeNotice(noticeId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/compliance-notices/${noticeId}/close`
    );

    return res.data.data;
  }

  /**
   * Close incident
   * @param incidentId
   */
  public async closeIncident(incidentId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/incidents/${incidentId}/close`
    );

    return res.data.data;
  }

  /**
   * Open incident
   * @param incidentId
   */
  public async OpenIncident(incidentId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/incidents/${incidentId}/open`
    );

    return res.data.data;
  }

  /**
   * Open non-compliance notice
   * @param noticeId
   */
  public async openNotice(noticeId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/compliance-notices/${noticeId}/open`
    );

    return res.data.data;
  }

  /**
   * Change register status (open/close)
   * @param registerId
   * @param status - status number
   */
  public async openCloseRegister(registerId: string, status: number): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/registers/items/${registerId}/status`,
        {
          status: status
        }
    );

    return res.data.data;
  }

  /**
   * Deactivate SWMS record
   * @param swmsId
   */
  public async deactivateSwms(swmsId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/swms/deactivate/${swmsId}`
    );

    return res.data.data;
  }

  /**
   * Activate SWMS record
   * @param swmsId
   */
  public async activateSwms(swmsId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/swms/activate/${swmsId}`
    );

    return res.data.data;
  }

  /**
   * Deactivate hazardous chemical
   * @param hazardousChemicalId
   */
  public async deactivateHazardousChemical(hazardousChemicalId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/hazardous-chemicals/${hazardousChemicalId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate Electrical Test and Tag
   * @param electricalId
   */
  public async deactivateElectrical(electricalId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/electrical/${electricalId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Deactivate Toolbox Talk Topic
   * @param toolboxTalkTopicId
   */
  public async deactivateToolboxTalkTopic(toolboxTalkTopicId: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/toolbox-talks/topics/deactivate/${toolboxTalkTopicId}`
    );

    return res.data.data;
  }

  /**
   * Changes the telephone number of the current user
   * @param userId (not used, kept for compatibility)
   * @param newTelephone
   */
  public async changeTelephone(userId: string, newTelephone: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/contacts/${this.userData.contactid}/telephone`,
        {
          newTelephone: newTelephone
        }
    );

    return res.data.data;
  }

  /**
   * Changes the Employee data
   * @param userId
   * @param newFirstName
   * @param newLastName
   * @param newTelephone
   * @param newAccountRoleCode
   */
  public async changeEmployee(
      userId: string,
      newFirstName: string,
      newLastName: string,
      newTelephone: string,
      newAccountRoleCode: number
  ): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/contacts/${userId}/employee`,
        {
          newFirstName: newFirstName,
          newLastName: newLastName,
          newTelephone: newTelephone,
          newAccountRoleCode: newAccountRoleCode
        }
    );

    return res.data.data;
  }

  /**
   * Set request flag
   * @param complianceId
   * @param flag
   */
  public async setRequestFlag(complianceId: string, flag: string): Promise<ChangePasswordResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/compliance/${complianceId}/request-flag`,
        {
          flag: flag
        }
    );

    return res.data.data;
  }

  /**
   * Changes the project details
   * @param projectId
   * @param projectName
   * @param jobNumber
   * @param supervisorContact
   * @param supervisorMobile
   * @param supervisorEmail
   * @param streetAddress
   * @param suburbAddress
   * @param stateAddress
   * @param lat
   * @param long
   */
  public async modifyProject(
      projectId: string,
      projectName: string,
      jobNumber: string,
      supervisorContact: string,
      supervisorMobile: string,
      supervisorEmail: string,
      streetAddress: string,
      suburbAddress: string,
      stateAddress: string,
      lat: string,
      long: string
  ): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/sites/${projectId}`,
        {
          projectName: projectName,
          jobNumber: jobNumber,
          supervisorContact: supervisorContact,
          supervisorMobile: supervisorMobile,
          supervisorEmail: supervisorEmail,
          streetAddress: streetAddress,
          suburbAddress: suburbAddress,
          stateAddress: stateAddress,
          lat: lat,
          long: long
        }
    );

    return res.data.data;
  }

  /**
   * Changes the signed in status of the current user
   * @param status
   */
  public async changeSignIn(status: boolean): Promise<ChangeSignedInResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/contacts/${this.userData.contactid}/sign-in`,
        {
          status: status
        }
    );

    return res.data.data;
  }

  /**
   * Kiosk sign out - sets timeout for site attendance
   * @param siteAttendanceId
   */
  public async kioskSignOut(siteAttendanceId: string): Promise<ChangeSignedInResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/attendance/${siteAttendanceId}/sign-out`
    );

    return res.data.data;
  }

  /**
   * Changes the name of the user
   * @param userId
   * @param newFirstName
   * @param newLastName
   */
  public async changeName(userId: string, newFirstName: string, newLastName: string): Promise<ChangeNameResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/contacts/${userId}/name`,
        {
          newFirstName: newFirstName,
          newLastName: newLastName
        }
    );

    return res.data.data;
  }

  /**
   * Changes the device and platform of the current user
   * @param userId
   * @param device
   * @param platform
   */
  public async changeDeviceInfo(userId: string, device: string, platform: string): Promise<ChangeNameResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/contacts/${userId}/device-info`,
        {
          device: device,
          platform: platform
        }
    );

    return res.data.data;
  }

  /**
   * Changes the site message
   * @param siteId
   * @param newSiteMessage
   */
  public async changeSiteMessage(siteId: string, newSiteMessage: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/sites/${siteId}/message`,
        {
          newSiteMessage: newSiteMessage
        }
    );

    return res.data.data;
  }

  /**
   * Changes the site status
   * @param status
   * @param siteId
   */
  public async changeSiteStatus(status: number, siteId: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/sites/${siteId}/status`,
        {
          status: status
        }
    );

    return res.data.data;
  }

  /**
   * Changes the compliance renew status
   * @param complianceId
   */
  public async changeComplianceRenewStatus(complianceId: string): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/compliance/${complianceId}/renew-status`
    );

    return res.data.data;
  }

  /**
   * Changes the Compliance Statement Date
   * @param date
   */
  public async changeComplianceStatementDate(date: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/compliance/statement-date/${this.userData.parent_account.accountid}`,
        {
          date: date
        }
    );

    return res.data.data;
  }

  /**
   * Change defect status (e.g., close defect by setting status to 804890001)
   * @param defectId
   * @param status
   */
  public async changeDefectStatus(defectId: string, status: number): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/defects/${defectId}/status`,
        {
          status: status
        }
    );

    return res.data.data;
  }

  /**
   * Assign Tool
   * @param toolId
   * @param contactId
   */
  public async assignTool(toolId: string, contactId: string): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/${toolId}/assign`,
        {
          contactId: contactId
        }
    );

    return res.data.data;
  }

  /**
   * Reserve Tool
   * @param toolId
   * @param contactId
   */
  public async reserveTool(toolId: string, contactId: string): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/tools/${toolId}/reserve`,
        {
          contactId: contactId
        }
    );

    return res.data.data;
  }

  /**
   * Send tool to maintenance
   * @param toolId
   */
  public async toolMaintenance(toolId: string): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/tools/${toolId}/maintenance`
    );

    return res.data.data;
  }

  /**
   * Remove tool assignment
   * @param toolId
   */
  public async removeToolAssignment(toolId: string): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/assets/tools/${toolId}/remove-assignment`
    );

    return res.data.data;
  }

  /**
   * Approve SWMS
   * @param swmsId
   * @param approverEmail
   */
  public async approveSwms(swmsId: string, approverEmail: string): Promise<ChangeRenewMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/swms/${swmsId}/approve`,
        {
          approverEmail: approverEmail
        }
    );

    return res.data.data;
  }

  /**
   * Gets the Subcontractors data of the current user
   */
  public async getSubcontractors(): Promise<ISubcontractorData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/subcontractors`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Inactive Subcontractors data of the current user
   */
  public async getInactiveSubcontractors(): Promise<ISubcontractorData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/subcontractors/inactive`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Subcontractors by ABN
   * @param abn
   */
  public async getSubcontractorsByAbn(abn: string): Promise<ISubcontractorData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/subcontractors/by-abn`,
        {
          params: {
            abn: abn
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Subcontractors by accountid
   * @param id
   */
  public async getSubcontractorsById(id: string): Promise<ISubcontractorData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/subcontractors/by-id`,
        {
          params: {
            id: id
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets connection status by ABN
   * @param abn
   */
  public async getConnection(abn: string): Promise<IConnectionData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/connection`,
        {
          params: {
            abn: abn,
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Change connection status of subcontractors
   * @param connectionId
   * @param status
   */
  public async changeConnectionStatus(connectionId: string, status: number): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/connections/${connectionId}/status`,
        {
          status: status
        }
    );

    return res.data.data;
  }

  /**
   * Change subcontractor name in connections
   * @param connectionId
   * @param name
   */
  public async changeSubcontractorName(connectionId: string, name: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/connections/${connectionId}/name`,
        {
          name: name
        }
    );

    return res.data.data;
  }

  /**
   * Change subcontractor email in connections
   * @param connectionId
   * @param email
   */
  public async changeSubcontractorEmail(connectionId: string, email: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/connections/${connectionId}/email`,
        {
          email: email
        }
    );

    return res.data.data;
  }

  /**
   * Change subcontractor phone in connections
   * @param connectionId
   * @param phone
   */
  public async changeSubcontractorPhone(connectionId: string, phone: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/connections/${connectionId}/phone`,
        {
          phone: phone
        }
    );

    return res.data.data;
  }

  /**
   * Change subcontractor account number in connections
   * @param connectionId
   * @param account
   */
  public async changeSubcontractorAccount(connectionId: string, account: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/connections/${connectionId}/account`,
        {
          account: account
        }
    );

    return res.data.data;
  }

  /**
   * Deactivate custom function
   * @param functionId
   */
  public async deactivateFunction(functionId: string): Promise<ChangeSiteMessageResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/implementation/function/${functionId}/deactivate`
    );

    return res.data.data;
  }

  /**
   * Toggle Project Menu Preference in Contact Record
   * @param preference
   */
  public async toggleProjectMenu(preference: string): Promise<ChangeTelephoneResult> {
    const res = await axiosInstance.patch(
        `${environment.backendApiUrl}/implementation/project-menu/${this.userData.contactid}`,
        {
          preference: preference
        }
    );

    return res.data.data;
  }

  public async getSites(sort: string, direction: string): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name,
            sort: sort,
            direction: direction
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the inactive Sites data of the current user
   * @param sort
   * @param direction
   */
  public async getInactiveSites(sort: string, direction: string): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/inactive`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name,
            sort: sort,
            direction: direction
          }
        }
    );

    return res.data.data.value;
  }

  public async getSitesPaged(params: {
    sort?: string;
    direction?: string;
    page?: number;
    pageSize?: number;
    statecode?: number;
    search?: string;
  }): Promise<{ value: ISiteData[]; totalCount: number; hasMore: boolean }> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/paged`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name,
            sort: params.sort || 'createdon',
            direction: params.direction || 'true',
            page: params.page || 1,
            pageSize: params.pageSize || 50,
            statecode: params.statecode ?? 0,
            ...(params.search ? { search: params.search } : {})
          }
        }
    );
    return res.data.data;
  }

  /**
   * Gets all Sites (active and inactive) for the current user's account
   * @param sort - field to sort by (default: 'new_name')
   * @param direction - sort direction ('true' = descending, 'false' = ascending)
   */
  public async getActiveInactiveSites(sort: string = 'new_name', direction: string = 'false'): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/active-inactive`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name,
            sort: sort,
            direction: direction // Now string: 'true' or 'false'
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Sites data of ALL users
   */
  public async getAllSites(): Promise<IAllSitesData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/all`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Sites data by ID
   * @param siteId
   */
  public async getSiteById(siteId: string): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/by-id/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Sites data by GUID
   * @param guid
   */
  public async getSiteByGuid(guid: string): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/by-guid/${guid}`
    );

    return res.data.data.value;
  }

  /**
   * Gets sites that are connected to current subcontractor
   */
  public async getSubcontractorSites(): Promise<IAllSitesData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/subcontractor`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets sites that are connected to a subcontractor by account ID
   * @param accountId
   */
  public async getSubcontractorSitesById(accountId: string): Promise<IAllSitesData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/subcontractor-by-id/${accountId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Connection ID
   * @param accountId
   */
  public async getConnectionId(accountId: string): Promise<IConnectionData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/connections/connection-id`,
        {
          params: {
            parentAccountId: this.userData.parent_account.accountid,
            accountId: accountId
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Connection Data
   * @param accountId
   */
  public async getConnectionData(accountId: string): Promise<IConnectionData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/connections/connection-data`,
        {
          params: {
            parentAccountId: this.userData.parent_account.accountid,
            accountId: accountId
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site incident reports using site GUID
   * @param siteId
   */
  public async getSiteIncidents(siteId: string): Promise<ISiteIncidents[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/incidents/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets messages from site id
   * @param siteId
   */
  public async getMessages(siteId: string): Promise<IMessages[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/messages/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Plant Registrations from plant GUID
   * @param guid
   */
  public async getPlantRegistrations(guid: string): Promise<IPlantRegistrations[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/plants/${guid}/registrations`
    );

    return res.data.data.value;
  }

  /**
   * Gets Plant Checklists from plant GUID
   * @param guid
   */
  public async getPlantChecklists(guid: string): Promise<IPlantChecklists[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/plants/${guid}/checklists`
    );

    return res.data.data.value;
  }

  /**
   * Gets plants belonging to a project using the site GUID
   * @param siteId
   */
  public async getProjectPlants(siteId: string): Promise<IPlantRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/plants/project/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets INACTIVE plants belonging to a project using the site GUID
   * @param siteId
   */
  public async getInactiveProjectPlants(siteId: string): Promise<IPlantRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/plants/project/${siteId}/inactive`
    );

    return res.data.data.value;
  }

  /**
   * Gets INACTIVE Connection ID
   * @param accountId
   */
  public async getInactiveConnectionId(accountId: string): Promise<IConnectionData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/connections/inactive-connection-id`,
        {
          params: {
            parentAccountId: this.userData.parent_account.accountid,
            accountId: accountId
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets list of registers from reference table
   * @param siteGuid
   * @param filter - category filter number
   */
  public async getRegistersFilter(siteGuid: string, filter: number): Promise<IReferences[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/registers/${siteGuid}`,
        {
          params: {
            filter: filter
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets list of registers from register table
   * @param siteGuid
   * @param filter - register name
   * @param category - category number
   */
  public async getRegisterItemsFilter(siteGuid: string, filter: string, category: number): Promise<IRegisterItems[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/registers/items/${siteGuid}`,
        {
          params: {
            filter: filter,
            category: category
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets list of INACTIVE registers from register table
   * @param siteGuid
   * @param filter - register name
   * @param category - category number
   */
  public async getInactiveRegisterItemsFilter(siteGuid: string, filter: string, category: number): Promise<IRegisterItems[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/registers/items/${siteGuid}/inactive`,
        {
          params: {
            filter: filter,
            category: category
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets list of workers currently signed in
   * @param siteId
   */
  public async getSignedInUsers(siteId: string): Promise<IContactData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/signed-in`,
        {
          params: {
            siteId: siteId
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Sites data by name
   * @param siteName
   */
  public async getSiteByName(siteName: string): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/by-name`,
        {
          params: {
            siteName: siteName
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets Scaffold Inspections
   * @param siteId
   */
  public async getScaffoldInspections(siteId: string): Promise<IScaffoldInspections[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inspections/inspection/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Formwork Prepour
   * @param siteId
   */
  public async getFormworkPrePour(siteId: string): Promise<IFormworkPrepour[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inspections/formwork-prepour/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Permits
   * @param siteId
   */
  public async getPermits(siteId: string): Promise<IPermits[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/documents/permits/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Employees data of the current user.
   * @param url
   */
  public async getEmployees(): Promise<IEmployeeData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/employees`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets ACTIVE Non compliance Notices of a given site
   * @param siteId
   */
  public async getNonComplianceNotices(siteId: string): Promise<INonComplianceNotices[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance-notices/non-compliance-notices/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets INACTIVE Non compliance Notices of a given site
   * @param siteId
   */
  public async getInactiveNonComplianceNotices(siteId: string): Promise<INonComplianceNotices[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance-notices/non-compliance-notices/${siteId}/inactive`
    );

    return res.data.data.value;
  }

  /**
   * Gets the email status of subcontractors
   * @param status
   */
  public async getEmailStatus(status: string): Promise<IEmailStatus[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/messages/status`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            status: status
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the training data for a contact
   * @param contactId
   */
  public async getTraining(contactId: string): Promise<ITraining[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/training/contact/${contactId}`
    );

    return res.data.data.value;
  }
  /**
   * Gets Site Inductions
   * @param siteId
   */
  public async getSiteInductions(siteId: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets APPROVED Site Specific Inductions
   * @param siteId
   */
  public async getSiteSpecificInductions(siteId: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/site-specific/${siteId}`,
        {
          params: {
            email: this.userData.emailaddress1
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets submitted Site Inductions
   * @param siteId
   */
  public async getSubmittedSiteInductions(siteId: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/submitted/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Site Inductions for a site (for subcontractors)
   * @param siteId
   * @param subcontractorId
   */
  public async getSubcontractorSiteInductions(siteId: string, subcontractorId: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/subcontractor/${siteId}/${subcontractorId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets all employees induction records for a given SWMS
   * @param swmsId
   */
  public async getSwmsInductions(swmsId: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/swms/${swmsId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets ALL site inductions belonging to a user
   * @param username
   */
  public async getUsersSiteInductions(username: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/user/${username}`
    );

    return res.data.data.value;
  }

  /**
   * Gets a list of sites within 1.1KM of the user
   * @param lat
   * @param long
   */
  public async getUserSites(lat: string, long: string): Promise<ISiteData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/sites/user-sites`,
        {
          params: {
            lat: lat,
            long: long
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets Site Inspections
   * @param siteId
   */
  public async getSiteInspections(siteId: string): Promise<ISiteInspections[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inspections/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets builder's generic site ID using 6 digit site id
   * @param siteId
   */
  public async getGenericSiteId(siteId: string): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/generic-site-id/${siteId}`
    );

    return res.data.data.value;
  }

  public async getResidentialGenericSiteIds(siteId: string): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/residential-generic-site-ids/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Site Subcontractors
   * @param siteId
   */
  public async getSiteSubcontractors(siteId: string): Promise<ISiteSubcontractors[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/site-subcontractors/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Site Hazards using site id
   * @param siteId
   */
  public async getSiteHazards(siteId: string): Promise<ISiteHazards[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/hazards/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Action List Hazards using site id (excludes closed hazards)
   * @param siteId
   */
  public async getActionListHazards(siteId: string): Promise<ISiteHazards[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/hazards/action-list/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Action List Site Inspections
   * @param siteId
   */
  public async getActionListSiteInspections(siteId: string): Promise<ISiteInspections[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inspections/action-list/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets OPEN Site incident reports using site GUID for action list
   * @param siteId
   */
  public async getActionListIncidents(siteId: string): Promise<ISiteIncidents[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/incidents/action-list/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets all SWMS of a site for Action List
   * (expired or pending status)
   * @param siteId
   */
  public async getActionListSWMS(siteId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/action-list/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Site Inductions for Action List
   * (expired or specific pending statuses)
   * @param siteId
   */
  public async getActionListSiteInductions(siteId: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/action-list/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets OPEN REQUESTED RESPONDED Non compliance Notices of a given site
   * (excludes closed status 804890001)
   * @param siteId
   */
  public async getActionListNonCompliance(siteId: string): Promise<INonComplianceNotices[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance-notices/action-list/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets list of contractors with no SWMS
   * @param siteId
   */
  public async getActionListNoSwms(siteId: string): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/no-swms/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets list of contractors with SWMS for a site
   * @param siteId
   */
  public async getAccountsWithSwms(siteId: string): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/with-swms/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets list of contractors for a site
   * @param siteId
   */
  public async getAccountsForSite(siteId: string): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/for-site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets list of SWMS with no inductions
   * @param siteId
   */
  public async getActionListSwmsNoInductions(siteId: string): Promise<ISiteSubcontractors[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/action-list/swms-no-inductions/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets OPEN REQUESTED RESPONDED defects of a given site
   * @param siteId
   */
  public async getActionListDefects(siteId: string): Promise<IDefectRecords[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/action-list/defects/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets plants with incomplete checklist
   * @param siteId
   */
  public async getActionListPlantChecklist(siteId: string): Promise<IPlantRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/action-list/plant-checklist/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets plants with overdue date
   * @param siteId
   */
  public async getActionListPlant(siteId: string): Promise<IPlantRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/action-list/plant/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets electrical items needing inspection
   */
  public async getActionListElectrical(): Promise<IElectricalRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/action-list/electrical`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets SDS for action list (expired)
   */
  public async getActionListSds(): Promise<IHazardousChemicalsRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/action-list/sds`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data of a site
   * @param siteId
   */
  public async getSiteAttendances(siteId: string): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data of a site ONLY FOR A SPECIFIC SUBCONTRACTOR
   * @param siteId
   */
  public async getSubcontractorSiteAttendance(siteId: string): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/subcontractor/${siteId}`,
        {
          params: {
            companyName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data of ALL SITES ONLY FOR A SPECIFIC SUBCONTRACTOR
   */
  public async getSubcontractorAllSiteAttendance(): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/subcontractor-all`,
        {
          params: {
            companyName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data of a site for TODAY
   * @param siteId
   */
  public async getTodaySiteAttendances(siteId: string): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/today/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data from SITE and FULL NAME (for KIOSK MODE)
   * @param siteGuid
   * @param fullName
   */
  public async getKioskAttendance(siteGuid: string, fullName: string): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/kiosk/${siteGuid}`,
        {
          params: {
            fullName: fullName
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets current SIGNED IN site (where user is currently checked in)
   */
  public async getUserSite(): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/current-site/${this.userData.contactid}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Implementation data of a builder using Account ID
   * @param accountId
   */
  public async getImplementation(accountId: string): Promise<IImplementations> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/implementation/${accountId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data of current user
   */
  public async getUserAttendance(): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/user/${this.userData.contactid}`,
        {
          params: {
            fullName: this.userData.fullname
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data of a company (today's attendance)
   */
  public async getCompanyAttendance(): Promise<ISiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/company`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site attendance data linked to sites
   */
  public async getLinkedAttendance(): Promise<ILinkedSiteAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/attendance/linked`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets compliance items for export
   */
  public async getComplianceForExport(): Promise<IComplianceItemData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance/export`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the list of compliance tasks
   */
  public async getComplianceTasks(): Promise<IComplianceTasks[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/compliance/tasks`
    );

    return res.data.data.value;
  }

  /**
   * Gets the SITE INDUCTIONS for export
   * @param siteGuid
   */
  public async getSiteInductionsExport(siteGuid: string): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/export/${siteGuid}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the SUBCONTRACTOR SITE INDUCTIONS for export
   */
  public async getSubcontractorSiteInductionsExport(): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/subcontractor-export`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
  * Gets the SITE SWMS for export
  * @param siteGuid
  */
  public async getSiteSwmsExport(siteGuid: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/export/${siteGuid}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the list of defects linked to a site
   * @param siteId
   */
  public async getSiteDefects(siteId: string): Promise<IDefectRecords[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/defects/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets the list of inactive defects linked to a site
   * @param siteId
   */
  public async getInactiveSiteDefects(siteId: string): Promise<IDefectRecords[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/defects/site/${siteId}/inactive`
    );

    return res.data.data.value;
  }

  /**
   * Gets the list of incidents linked to a subcontractor
   * @param accountId
   */
  public async getIncidentReports(accountId: string): Promise<IIncidentReports[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/incidents/reports`,
        {
          params: {
            accountId: accountId
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the list of subcontractors who have SWMSes at a site
   * @param siteId
   */
  public async getSubcontractorSwms(siteId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/subcontractor/${siteId}`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets SWMS of a subcontractor at a site using site ID and account ID
   * @param siteId
   * @param accountId
   */
  public async getSWMS(siteId: string, accountId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/${siteId}/${accountId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets SWMS data using swms id
   * @param swmsId
   */
  public async getSWMSById(swmsId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/by-id/${swmsId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets all SWMS of a site
   * @param siteId
   */
  public async getSiteSWMS(siteId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets all inactive SWMS of a site
   * @param siteId
   */
  public async getInactiveSiteSWMS(siteId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/site/${siteId}/inactive`
    );

    return res.data.data.value;
  }

  /**
   * Gets SWNS of a site based on user's parent account
   * @param siteId
   * @param accountId
   */
  public async getSubcontractorSiteSWMS(siteId: string, accountId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/subcontractor-site/${siteId}/${accountId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets all swms linked to induction
   * @param inductionId
   */
  public async getInductionSwms(inductionId: string): Promise<ISwmsData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/swms/induction/${inductionId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Plant Registers for current account
   */
  public async getPlantRegisters(): Promise<IPlantRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/plant`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets Toolbox Talk Topics.
   * @param siteId
   */
  public async getToolboxTalkTopics(siteId: string): Promise<IToolboxTalkTopics[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/toolbox-talks/topics/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Plant Registers from builder (accountid).
   * @param accountId
   */
  public async getPlantRegistersFromBuilder(accountId: string): Promise<IPlantRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/plant/builder/${accountId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Electrical Test Registers for current account
   */
  public async getElectricalTestRegisters(): Promise<IElectricalRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/electrical`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets Electrical Test Registers for a specific site
   * @param siteId
   */
  public async getSiteElectrical(siteId: string): Promise<IElectricalRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/electrical/site/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Tools for current account
   */
  public async getTools(): Promise<ITools[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/tools`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets tool history (audit trail)
   * @param toolId
   */
  public async getToolHistory(toolId: string): Promise<any[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/tools/${toolId}/history`
    );

    return res.data.data.value;
  }

  /**
   * Gets Safety Data Sheets (SDS) / Hazardous Chemicals for current account
   */
  public async getHazardousChemicals(): Promise<IHazardousChemicalsRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/chemicals`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets Site SDS / Hazardous Chemicals for a specific site
   * @param siteId
   */
  public async getSiteSds(siteId: string): Promise<IHazardousChemicalsRegister[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/assets/chemicals/site/${siteId}`
    );

    return res.data.data.value;
  }

  public async getPrincipalContractors(): Promise<IAccountData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/accounts/principal-contractors`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  public async getTrainingForExport(): Promise<ITraining[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/training/export`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets all Site Inductions for Export (for current account)
   */
  public async getInductionsExport(): Promise<ISiteInductions[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/inductions/export`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Risk Assessments for current account
   */
  public async getRiskAssessments(): Promise<IRiskAssessmentData[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/risk-assessments`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Sites data of the current user.
   * @param url
   */
  public async getCompliance(): Promise<IComplianceItemData[]> {
    return await this.getComplianceDetails(this.userData.parent_account.accountid);
  }

  /**
   * Gets the Site attendance data of the current user.
   * @param url
   */
  public async getUserApps(): Promise<IUserApps[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/documents/user-apps`,
        {
          params: {
            accountId: this.userData.parent_account.accountid,
            accountName: this.userData.parent_account.name
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets the Site Documents
   * @param siteId
   */
  public async getSiteDocuments(siteId: string): Promise<IUserApps[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/documents/site/${siteId}`
    );

    return res.data.data.value;
  }

  /*
   * Gets the contact details from username
   */
  public async getContact(username: string): Promise<IContactData> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/auth/user/by-username`,
        { params: { username } }
    );
    return res.data.user;
  }

  /*
   * Gets the contact details from email
   */
  public async getContactbyEmail(email: string): Promise<IContactData> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/auth/user/by-email`,
        { params: { email } }
    );
    return res.data.user;
  }

  /*
   * Gets the contact details from contact id
   */
  public async getContactId(contactid: string): Promise<IContactData> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/auth/user/by-id`,
        { params: { id: contactid } }
    );
    return res.data.user;
  }

  /**
   * Gets ALL toolbox talks
   * @param url
   */
  public async getToolboxTalks(siteid: string): Promise<IToolboxTalks[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/toolbox-talks/${siteid}`
    );

    return res.data.data.value;
  }

  /**
   * Gets Subcontractor toolbox talks for current account
   */
  public async getSubcontractorToolboxTalks(): Promise<IToolboxTalks[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/toolbox-talks/subcontractor`,
        {
          params: {
            accountId: this.userData.parent_account.accountid
          }
        }
    );

    return res.data.data.value;
  }

  /**
   * Gets Open toolbox talks for a site
   * @param siteId
   */
  public async getOpenToolboxTalks(siteId: string): Promise<IToolboxTalks[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/toolbox-talks/open/${siteId}`
    );

    return res.data.data.value;
  }

  /**
   * Gets toolbox talk by ID
   * @param toolboxTalkId
   */
  public async getToolboxTalk(toolboxTalkId: string): Promise<IToolboxTalkData> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/toolbox-talks/by-id/${toolboxTalkId}`
    );

    return res.data.data.value; // Беремо перший елемент з масиву
  }

  /**
   * Gets toolbox talk attendance
   * @param toolboxTalkId
   */
  public async getToolboxTalkAttendance(toolboxTalkId: string): Promise<ItoolboxTalkAttendance[]> {
    const res = await axiosInstance.get(
        `${environment.backendApiUrl}/toolbox-talks/attendance/${toolboxTalkId}`
    );

    return res.data.data.value;
  }

  /**
   *
   * Gets document links
   * file_sp_link = the public sharepoint link
   *
   */
  public getSPFile(
    opt: {
      file_sp_link: string;
      file_src_link: string;
      file_name: string;
    },
    download = false
  ) {
    // window.open(opt.file_sp_link);
    window.open(
      `${CONSTANTS.apiConfig.apiUrl}/file?spUrl=${opt.file_sp_link}&fileUrl=${
        opt.file_src_link
      }&filename=${encodeURIComponent(opt.file_name)}` + (download ? '&download=1' : ''),
      '_blank'
    );
  }

  /**
   * Removes the session info of the user.
   */
  public logout(): void {
    this.storage.removeCommonObject('authData');
    this.storage.removeCommonObject('userData');
    this.storage.removeCommonObject('subcontractorsData');
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('jwtExpires');
  }

  public async login(username: string, password: string): Promise<IContactData> {
    try {
      const response = await axiosInstance.post(
          `${environment.backendApiUrl}/auth/login`,
          { username, password }
      );

      const { user, token, expiresAt } = response.data;

      this.storage.setLocalObject('userData', user);
      localStorage.setItem('jwtToken', token);
      localStorage.setItem('jwtExpires', String(expiresAt));

      return user;

    } catch (error) {
      console.error('Login error:', error);
      return undefined;
    }
  }

  public async tokenLogin(username: string, token: string): Promise<IContactData> {
    try {
      const response = await axiosInstance.post(
          `${environment.backendApiUrl}/auth/token-login`,
          { username, token }
      );

      const { user, token: jwtToken, expiresAt } = response.data;

      this.storage.setLocalObject('userData', user);
      localStorage.setItem('jwtToken', jwtToken);
      localStorage.setItem('jwtExpires', String(expiresAt));

      return user;

    } catch (error: any) {
      console.error('Token login error:', error);
      return undefined;
    }
  }

  //SECTION FOR BATCH ENCRYPTION OF PASSWORDS

  public async migrateHashPassword() {
    await axiosInstance.post(
        '/auth/admin/migrate-passwords'
    );
  }
}